import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

/**
 * Batch generation for the branch: every eligible vendor is considered in one
 * call. gl-api never aborts the run for one vendor -- it returns what was
 * created and what was skipped, with a fixed reason code per skip.
 */

const currentYear = new Date().getFullYear();
const TAX_YEARS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

interface VendorOption {
  id: number;
  vendor_code: string;
  display_name: string;
}

interface Props {
  show: boolean;
  saving: boolean;
  vendors: VendorOption[];
  onClose: () => void;
  onGenerate: (taxYear: number, vendorIds: number[]) => void;
}

const GenerateFormsModal: React.FC<Props> = ({
  show, saving, vendors, onClose, onGenerate,
}) => {
  const { t } = useTranslation('Tax1099Forms');
  const [taxYear, setTaxYear] = useState(String(currentYear));
  const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([]);

  const toggleVendor = (vendorId: number) => {
    setSelectedVendorIds((previous) => (
      previous.includes(vendorId)
        ? previous.filter((id) => id !== vendorId)
        : [...previous, vendorId]
    ));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onGenerate(Number(taxYear), selectedVendorIds);
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{t('generate.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('generate.tax_year')}</Form.Label>
            <Form.Select value={taxYear} onChange={(event) => setTaxYear(event.target.value)}>
              {TAX_YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Label>{t('generate.vendors_label')}</Form.Label>
          <p className="text-muted small">{t('generate.vendors_help')}</p>
          <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {vendors.length === 0 && (
              <p className="text-muted small mb-0">{t('generate.no_vendors')}</p>
            )}
            {vendors.map((vendor) => (
              <Form.Check
                key={vendor.id}
                type="checkbox"
                id={`generate-vendor-${vendor.id}`}
                label={`${vendor.vendor_code} — ${vendor.display_name}`}
                checked={selectedVendorIds.includes(vendor.id)}
                onChange={() => toggleVendor(vendor.id)}
              />
            ))}
          </div>
          <Form.Text muted>
            {selectedVendorIds.length === 0
              ? t('generate.all_vendors_selected')
              : t('generate.n_vendors_selected', { count: selectedVendorIds.length })}
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('generate.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('generate.generating') : t('generate.generate')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GenerateFormsModal;
